import { useState, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { Play, Pause } from 'lucide-react';
import { Item, CandidateProfile } from '../lib/api';
import { formatPrice } from '../lib/i18n';
import { haptic } from '../lib/telegram';
import { SwipeCardTemplate, Badge } from './SwipeCardTemplate';
import { detectMediaPreview, MediaPreviewInfo } from '../lib/media';
import { useTimeOnCard } from '../hooks/useTimeOnCard';

interface SwipeCardProps {
  item: Item;
  onSwipe: (direction: 'left' | 'right') => void;
  onInfo: () => void;
  variant?: 'default' | 'profile';
}

export function SwipeCard({ item, onSwipe, onInfo, variant = 'default' }: SwipeCardProps) {
  const [{ x, rotate }, api] = useSpring(() => ({ x: 0, rotate: 0 }));
  const [swiping, setSwiping] = useState<'left' | 'right' | null>(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [activeMediaInfo, setActiveMediaInfo] = useState<MediaPreviewInfo | null>(null);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const controlsFadeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const candidate = (item as { candidate?: CandidateProfile }).candidate;
  useTimeOnCard(item?.id, { eventType: 'view', minDurationMs: 1500 });
  
  // Cleanup timers
  useEffect(() => {
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
      if (controlsFadeTimerRef.current) {
        clearTimeout(controlsFadeTimerRef.current);
      }
    };
  }, []);
  
  // Reset state when item changes (new card appears)
  useEffect(() => {
    setSwiping(null);
    setShowControls(false);
    api.start({ x: 0, rotate: 0, immediate: true });
    
    // Clear any pending timers
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }
    if (controlsFadeTimerRef.current) {
      clearTimeout(controlsFadeTimerRef.current);
    }
  }, [item.id, api]);
  
  const bind = useDrag(
    ({ active, movement: [mx], direction: [xDir], velocity: [vx] }) => {
      const trigger = vx > 0.2 || Math.abs(mx) > 100;
      
      if (active) {
        api.start({ x: mx, rotate: mx / 20 });
        
        if (mx > 50) {
          setSwiping('right');
          haptic.light();
        } else if (mx < -50) {
          setSwiping('left');
          haptic.light();
        } else {
          setSwiping(null);
        }
      } else {
        if (trigger) {
          const direction = xDir < 0 ? 'left' : 'right';
          api.start({ 
            x: direction === 'left' ? -500 : 500, 
            rotate: direction === 'left' ? -45 : 45,
          });
          setTimeout(() => {
            onSwipe(direction);
            api.start({ x: 0, rotate: 0, immediate: true });
            setSwiping(null);
          }, 300);
        } else {
          api.start({ x: 0, rotate: 0 });
          setSwiping(null);
        }
      }
    },
    { axis: 'x' }
  );
  
  const isProfile = variant === 'profile';

  const previewSource = useMemo(() => {
    const link = (item as unknown as { link?: string }).link;
    const value = (item as unknown as { value?: string }).value;
    return item.preview_url || link || value || item.media_url || undefined;
  }, [item]);

  const mediaInfo = useMemo(() => detectMediaPreview(previewSource), [previewSource]);

  useEffect(() => {
    setIsPlayingPreview(false);
    setActiveMediaInfo(null);

    const node = mediaRef.current;
    if (node) {
      try {
        node.pause();
        node.currentTime = 0;
      } catch (error) {
        // ignore cleanup errors
      }
    }

    if (mediaInfo.type !== 'none' && mediaInfo.type !== 'direct-link') {
      setActiveMediaInfo(mediaInfo);
      setIsPlayingPreview(true);
    }
  }, [item.id, mediaInfo]);

  useEffect(() => {
    const node = mediaRef.current;
    if (!node) return;
    if (isPlayingPreview) {
      node.play().catch(() => {});
    } else {
      try {
        node.pause();
        node.currentTime = 0;
      } catch (error) {
        // ignore
      }
    }
  }, [isPlayingPreview]);

  useEffect(() => {
    return () => {
      const node = mediaRef.current;
      if (!node) return;
      try {
        node.pause();
      } catch (error) {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    if (!isPlayingPreview) {
      mediaRef.current = null;
    }
  }, [isPlayingPreview]);

  const badges: Badge[] = (() => {
    if (isProfile) {
      const candidateTags = candidate
        ? [...(candidate.roles_display || candidate.roles || []), ...(candidate.styles || [])]
        : item.tags || [];
      const tagArray = Array.from(
        new Set(candidateTags.map((t) => String(t).trim()).filter(Boolean))
      );
      return tagArray.map((tag) => ({ text: tag, variant: 'green' as const }));
    }
    const list: Badge[] = [];
    if (item.bpm) list.push({ text: `${item.bpm} BPM`, variant: 'default' });
    if (item.key) list.push({ text: item.key, variant: 'default' });
    if (item.is_exclusive) list.push({ text: 'Exclusive', variant: 'purple' });
    return list;
  })();

  const effectiveMediaInfo = useMemo(() => {
    if (isPlayingPreview && activeMediaInfo) {
      return activeMediaInfo;
    }
    return mediaInfo;
  }, [activeMediaInfo, isPlayingPreview, mediaInfo]);

  const handlePlayToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (mediaInfo.type === 'none') {
      haptic.light();
      return;
    }

    if (mediaInfo.type === 'direct-link' && mediaInfo.rawUrl) {
      haptic.light();
      window.open(mediaInfo.rawUrl, '_blank');
      return;
    }

    haptic.medium();
    if (isPlayingPreview) {
      setIsPlayingPreview(false);
      setActiveMediaInfo(null);
    } else {
      setActiveMediaInfo(mediaInfo);
      setIsPlayingPreview(true);
    }
    
    // Show controls and reset fade timer
    setShowControls(true);
    if (controlsFadeTimerRef.current) {
      clearTimeout(controlsFadeTimerRef.current);
    }
    controlsFadeTimerRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const handleCardClick = () => {
    // Double-click logic for card body
    clickCountRef.current += 1;

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (clickCountRef.current === 1) {
      // First click - show controls
      clickTimerRef.current = setTimeout(() => {
        if (mediaInfo.type !== 'none') {
          setShowControls(true);
          if (controlsFadeTimerRef.current) {
            clearTimeout(controlsFadeTimerRef.current);
          }
          controlsFadeTimerRef.current = setTimeout(() => {
            setShowControls(false);
          }, 3000);
        }
        clickCountRef.current = 0;
      }, 300);
    } else if (clickCountRef.current === 2) {
      // Second click - toggle play/pause
      clickCountRef.current = 0;
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
      handlePlayToggle({ stopPropagation: () => {} } as React.MouseEvent);
    }
  };

  let mediaInner: ReactNode = null;
  if (isPlayingPreview) {
    switch (effectiveMediaInfo.type) {
      case 'youtube':
      case 'spotify':
      case 'soundcloud':
        if (effectiveMediaInfo.embedUrl) {
          mediaRef.current = null;
          mediaInner = (
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <iframe
                key={`${item.id}-${effectiveMediaInfo.embedUrl}`}
                src={effectiveMediaInfo.embedUrl}
                className="w-full"
                style={{ minHeight: effectiveMediaInfo.type === 'spotify' ? 180 : 200 }}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        break;
      case 'video':
        if (effectiveMediaInfo.embedUrl) {
          mediaInner = (
            <video
              ref={(node) => {
                mediaRef.current = node;
              }}
              src={effectiveMediaInfo.embedUrl}
              className="w-full rounded-2xl border border-white/10"
              controls
              playsInline
            />
          );
        }
        break;
      case 'audio':
        if (effectiveMediaInfo.embedUrl) {
          mediaInner = (
            <audio
              ref={(node) => {
                mediaRef.current = node;
              }}
              src={effectiveMediaInfo.embedUrl}
              className="w-full"
              controls
            />
          );
        }
        break;
      default:
        break;
    }
  }

  const mediaContent = mediaInner ? (
    <div className="bg-black/20 border border-white/10 rounded-2xl px-4 py-3">
      {mediaInner}
    </div>
  ) : undefined;

  const playButtonIcon = mediaInfo.type === 'direct-link' ? 'link' : 'play';
  const showPrice = !isProfile;
  const showPlayButton = mediaInfo.type !== 'none' && showControls;
  const playButtonStyle = 'default';
  const alwaysShowPlayButton = false;

  const title = isProfile
    ? candidate?.name || (candidate?.username ? `@${candidate.username}` : 'Digital Artisan')
    : undefined;
  const subtitle = isProfile
    ? candidate?.username
      ? `@${candidate.username}`
      : candidate?.city || item.creator?.username || undefined
    : undefined;
  const description = isProfile ? candidate?.bio || item.description : undefined;
  const categoryLabel = isProfile
    ? (candidate?.roles_display?.[0]
        || (item as { candidateCategoryLabel?: string }).candidateCategoryLabel
        || candidate?.roles?.[0]
        || String(item.category || ''))
    : undefined;
  return (
    <animated.div 
      {...bind()} 
      style={{ x, rotate }} 
      className="absolute inset-0"
      onClick={handleCardClick}
    >
      <SwipeCardTemplate
        item={item}
        swiping={swiping}
        formattedPrice={formatPrice(item.price_uah / 100)}
        onPlayClick={handlePlayToggle}
        onInfoClick={(e) => {
          e.stopPropagation();
          onInfo();
          haptic.light();
        }}
        badges={badges}
        showPrice={showPrice}
        showInfoButton={true}
        playButtonStyle={playButtonStyle}
        title={title}
        subtitle={subtitle}
        description={description}
        categoryLabel={categoryLabel}
        alwaysShowPlayButton={alwaysShowPlayButton}
        isPlaying={isPlayingPreview}
        playButtonIcon={playButtonIcon}
        mediaContent={mediaContent}
        showPlayButton={showPlayButton}
        imageSrc={isProfile ? candidate?.photo_url : undefined}
      />
      
      {/* Play/Pause indicator when controls are hidden */}
      {!showControls && mediaInfo.type !== 'none' && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20">
          {isPlayingPreview ? (
            <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-xl flex items-center justify-center opacity-0 animate-pulse">
              <Pause className="w-8 h-8 text-white" fill="currentColor" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-splyt-green/80 backdrop-blur-xl flex items-center justify-center glow-green">
              <Play className="w-8 h-8 text-splyt-dark ml-1" fill="currentColor" />
            </div>
          )}
        </div>
      )}
    </animated.div>
  );
}



